import { Trans } from '@lingui/react/macro';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import { setCookie } from '@tanstack/react-start/server';
import { AlertCircle } from '@untitledui/icons/AlertCircle';
import { CheckCircle } from '@untitledui/icons/CheckCircle';
import { Loading02 } from '@untitledui/icons/Loading02';
import { Mail01 } from '@untitledui/icons/Mail01';
import { z } from 'zod';

import { authControllerVerifyEmail } from '@/api/core-api';
import { Button, LinkButton } from '@/components/base/buttons/button';
import { AuthLayout } from '@/routes/participants/-components/auth-layout';
import { DecorativeCircles } from '@/routes/participants/-components/decorative-circles';
import { assertClient, getClient } from '@/utils/get-api-client';
import {
  buildSessionCookie,
  createSession,
} from '@/utils/session/create-session';

const inputSchema = z.object({ userId: z.string(), token: z.string() });
const verifyResearcherEmail = createServerFn()
  .validator((data: z.infer<typeof inputSchema>) => inputSchema.parse(data))
  .handler(async ({ data: { userId, token } }) => {
    const client = getClient();
    assertClient(client);
    const response = await authControllerVerifyEmail({
      client,
      body: { userId, token },
    });

    if (response.error || !response.data) {
      const status = response.status ?? 500;
      const reason =
        status === 404
          ? 'not-found'
          : status === 400
            ? 'token-mismatch'
            : 'unknown';
      throw new Error(reason);
    }

    const sessionId = await createSession(response.data.id);
    const cookie = buildSessionCookie(sessionId);
    setCookie(cookie.name, cookie.value, cookie.options);

    return response.data.id;
  });

export const Route = createFileRoute(
  '/researchers/verify-email/$userId/account/$token',
)({ component: VerifyEmailTokenPage });

function VerifyEmailTokenPage() {
  const { userId, token } = Route.useParams();
  const mutation = useMutation({
    mutationFn: ({ userId, token }: { userId: string; token: string }) =>
      Promise.all([
        // Minimum delay to avoid a flicker
        new Promise((resolve) => setTimeout(resolve, 1000)),
        verifyResearcherEmail({ data: { userId, token } }),
      ]),
  });

  return (
    <AuthLayout rightPanel={<ResearcherVerifyPanel />}>
      <div className="flex flex-col items-center text-center">
        <DecorativeCircles>
          {mutation.status === 'idle' && (
            <div className="relative z-10 flex size-14 items-center justify-center rounded-full bg-brand-solid">
              <Mail01 className="size-7 text-white" />
            </div>
          )}

          {mutation.status === 'pending' && (
            <div className="relative z-10 flex size-14 items-center justify-center rounded-full bg-brand-solid">
              <Loading02 className="size-7 animate-spin text-white" />
            </div>
          )}

          {mutation.status === 'success' && (
            <div className="relative z-10 flex size-14 scale-150 items-center justify-center rounded-full bg-success-solid duration-500 animate-in fade-in zoom-in-50">
              <CheckCircle className="size-7 text-white" />
            </div>
          )}

          {mutation.status === 'error' && (
            <div className="relative z-10 flex size-14 items-center justify-center rounded-full bg-error-solid">
              <AlertCircle className="size-7 text-white" />
            </div>
          )}
        </DecorativeCircles>

        <h1 className="z-10 mt-2 font-heading text-display-sm font-semibold text-primary">
          {mutation.status === 'idle' && (
            <span>
              <Trans>Confirm your email</Trans>
            </span>
          )}
          {mutation.status === 'pending' && (
            <span>
              <Trans>Verifying your email...</Trans>
            </span>
          )}
          {mutation.status === 'success' && (
            <span>
              <Trans>Email verified!</Trans>
            </span>
          )}
          {mutation.status === 'error' && (
            <span>
              <Trans>Verification failed</Trans>
            </span>
          )}
        </h1>

        <p className="z-10 mt-3 text-md text-primary">
          {mutation.status === 'idle' && (
            <span>
              <Trans>
                Click the button below to verify your email and finish setting
                up your researcher workspace.
              </Trans>
            </span>
          )}
          {mutation.status === 'pending' && (
            <span>
              <Trans>Please wait while we verify your email address.</Trans>
            </span>
          )}
          {mutation.status === 'success' && (
            <span>
              <Trans>Your email has been successfully verified</Trans>
            </span>
          )}
          {mutation.status === 'error' && (
            <span>
              <FailureMessage reason={mutation.error.message} />
            </span>
          )}
        </p>

        {mutation.status === 'idle' && (
          <Button
            color="primary"
            size="lg"
            className="z-10 mt-8 w-full"
            isLoading={mutation.isPending}
            isDisabled={mutation.isPending}
            onClick={() => mutation.mutate({ userId, token })}
            data-testid="researcher-verify-email-button"
          >
            <Trans>Verify email</Trans>
          </Button>
        )}

        {mutation.status === 'success' && (
          <LinkButton
            color="primary"
            size="lg"
            className="z-10 mt-8 w-full"
            to="/researchers/onboarding"
            reloadDocument
            data-testid="researcher-verify-next-button"
          >
            <Trans>Next</Trans>
          </LinkButton>
        )}

        {mutation.status === 'error' && (
          <LinkButton
            color="primary"
            size="lg"
            to="/researchers/login"
            className="z-10 mt-8"
          >
            <Trans>Go to login</Trans>
          </LinkButton>
        )}
      </div>
    </AuthLayout>
  );
}

const FailureMessage = ({ reason }: { reason: string }) => (
  <>
    {reason === 'token-mismatch' && (
      <Trans>
        This verification link is invalid or has already been used. Try logging
        in, or request a new verification email.
      </Trans>
    )}
    {reason === 'not-found' && (
      <Trans>
        We couldn&apos;t find an account for this verification link.
      </Trans>
    )}
    {reason === 'unknown' && (
      <Trans>
        Something went wrong verifying your email. Please try again.
      </Trans>
    )}
  </>
);

function ResearcherVerifyPanel() {
  return (
    <div className="relative flex h-full min-h-180 flex-col justify-between overflow-hidden bg-brand-section px-10 py-12 text-white">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.22),transparent_34%),radial-gradient(circle_at_80%_0%,rgba(255,255,255,0.14),transparent_30%)]" />
      <div className="relative">
        <p className="text-sm font-semibold tracking-[0.16em] text-white/70 uppercase">
          <Trans>Welcome aboard</Trans>
        </p>
        <h2 className="mt-4 max-w-md font-heading text-display-md font-semibold text-white">
          <Trans>One quick check, then you&apos;re in.</Trans>
        </h2>
        <p className="mt-4 max-w-md text-lg text-white/80">
          <Trans>
            We verify your email so we can keep your workspace secure and let
            other teammates reach you.
          </Trans>
        </p>
      </div>
    </div>
  );
}
